import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { CallText } from './CallText';
import { EnterAllBidsDialog } from './EnterAllBidsDialog';
import { normalizeCall } from '../../utils/notesSearch';

/** Imperative surface used by the page (the external bid-picker writes into the active box). */
export interface BiddingSequenceBuilderHandle {
  /** Fill the left-most empty box with a call token (e.g. `1D`, `Pass`, `X`). */
  addBid: (call: string) => void;
  /** Flag every empty box (red glow) and return whether all boxes are filled. */
  validate: () => boolean;
  /** Reset to a single empty opening box, clearing highlights. */
  reset: () => void;
}

interface BiddingSequenceBuilderProps {
  /** Notified whenever the sequence of boxes changes (drives legal-bid computation + search). */
  onSequenceChange?: (boxes: string[]) => void;
  /**
   * Called when a box is clicked/tapped. Boxes are not text-editable — the page uses this to move
   * focus to the bid-input control so the learner enters bids there (FR-017).
   */
  onBoxActivate?: () => void;
}

const EM_DASH = '—';

function endsInThreePasses(boxes: string[]): boolean {
  if (boxes.length < 3) return false;
  return boxes.slice(-3).every((b) => b !== '' && normalizeCall(b).kind === 'pass');
}

/**
 * Feature 022 — the bidding-sequence container. Read-only call boxes separated by an em-dash, with a
 * circular "+" add control. Bids are entered only via the external picker (`addBid`); boxes can only
 * be cleared. The add control is disabled until every box is filled and after three consecutive
 * passes; activating it while disabled shows the "Enter all bids to proceed" popup (FR-012..FR-017).
 */
export const BiddingSequenceBuilder = forwardRef<
  BiddingSequenceBuilderHandle,
  BiddingSequenceBuilderProps
>(function BiddingSequenceBuilder({ onSequenceChange, onBoxActivate }, ref) {
  const [boxes, setBoxes] = useState<string[]>(['']);
  const [invalid, setInvalid] = useState<boolean[]>([false]);
  const [popupOpen, setPopupOpen] = useState(false);
  const onChangeRef = useRef(onSequenceChange);
  onChangeRef.current = onSequenceChange;

  useEffect(() => {
    onChangeRef.current?.(boxes);
  }, [boxes]);

  const allFilled = boxes.every((b) => b !== '');
  const addEnabled = allFilled && !endsInThreePasses(boxes);

  useImperativeHandle(
    ref,
    () => ({
      addBid: (call: string) => {
        setBoxes((prev) => {
          const target = prev.indexOf('');
          if (target === -1) return prev; // no empty box — must press "+" first
          const next = [...prev];
          next[target] = call;
          return next;
        });
        setInvalid((prev) => {
          const target = boxes.indexOf('');
          if (target === -1) return prev;
          const next = [...prev];
          next[target] = false;
          return next;
        });
      },
      validate: () => {
        const flags = boxes.map((b) => b === '');
        setInvalid(flags);
        return flags.every((f) => !f);
      },
      reset: () => {
        setBoxes(['']);
        setInvalid([false]);
      },
    }),
    [boxes],
  );

  const clearBox = useCallback((index: number) => {
    setBoxes((prev) => {
      const next = [...prev];
      next[index] = '';
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    if (!addEnabled) {
      setPopupOpen(true);
      return;
    }
    setBoxes((prev) => [...prev, '']);
    setInvalid((prev) => [...prev, false]);
  }, [addEnabled]);

  return (
    <div>
      <div className="text-sm text-ink-soft mb-2">Bidding sequence:</div>
      <div className="bg-cream border border-line rounded-lg p-3">
        <div className="flex flex-wrap items-center gap-2">
          {boxes.map((box, i) => (
            <div key={i} className="flex items-center gap-2">
              {i > 0 && (
                <span className="text-ink-soft" aria-hidden="true">
                  {EM_DASH}
                </span>
              )}
              <div
                data-testid={`bid-box-${i}`}
                onClick={() => onBoxActivate?.()}
                className={`min-h-[44px] min-w-[80px] px-3 py-2 rounded-md bg-card border flex items-center justify-between gap-2 cursor-pointer ${
                  invalid[i]
                    ? 'border-red-500 ring-2 ring-red-400 shadow-[0_0_8px_rgba(239,68,68,0.6)]'
                    : 'border-line-strong'
                }`}
              >
                <span className="font-medium text-ink">
                  {box === '' ? <span className="text-ink-soft">&nbsp;</span> : <CallText call={box} />}
                </span>
                {box !== '' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // clearing must not also refocus the picker
                      clearBox(i);
                    }}
                    aria-label={`Clear bid ${i + 1}`}
                    className="text-ink-soft hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          <span className="text-ink-soft" aria-hidden="true">
            {EM_DASH}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            aria-label="Add another bid"
            aria-disabled={!addEnabled}
            className={`h-11 w-11 rounded-full flex items-center justify-center border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              addEnabled
                ? 'border-primary-600 text-primary-600 hover:bg-primary-50'
                : 'border-line-strong text-ink-soft cursor-default'
            }`}
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <EnterAllBidsDialog isOpen={popupOpen} onClose={() => setPopupOpen(false)} />
    </div>
  );
});
